import dayjs from '../../../../shared/services/dayjs.js';
import { schichtenService } from '../services/SchichtenService.js';
import { secureHTML } from '../util.js';
import { erstelleZeitblockTabelle } from '../zeitblock.js';

// Platzhalter des Single-Trigger-Selects (Vorlage-Editor) — macht die "Neue Vorlage anlegen …"-Option
// im Popover-Fußbereich auch dann sichtbar, wenn man den Trigger selbst noch nicht geöffnet hat.
const VORLAGE_TRIGGER_PLATZHALTER = 'Vorlage wählen / Neue Vorlage erstellen';

/**
 * Schnellauswahl Vorlage — Chips für die (bis zu drei) meistgenutzten Vorlagen des Bereichs plus eine
 * durchsuchbare, nach Bereich gruppierte "Weitere …"-Popover für den Rest. Ersetzt das alte reine
 * <select> in #input-vorlagen — im Schicht-Editor zum Anwenden einer Vorlage auf die offene Schicht
 * (siehe EDITORS.md §0/§3), im Vorlage-Editor zum Laden einer vorhandenen Vorlage in den Editor
 * ("Vorhandene Vorlage laden").
 *
 * Vereinfachung ggü. dem Design-Handoff: Die drei Vorschlags-Chips werden nicht nach echter
 * Nutzungshäufigkeit pro Mitarbeiter der letzten 8 Wochen sortiert (dafür gibt es aktuell keine
 * Nutzungs-Tracking-Daten), sondern schlicht nach der vom Server gelieferten Reihenfolge für den
 * Bereich. Die "Abwesenheit"-Gruppe (Krank/Urlaub) aus dem Handoff fehlt hier bewusst — das Anwenden
 * einer EGFZ-Vorlage auf eine bereits offene, konkrete Schicht ist ein eigener Ablauf
 * (erstelleEGFZSchicht) und wird von dieser Schnellauswahl (nur echte Vorlagen) nicht abgedeckt.
 *
 * @param {HTMLElement} container die #input-vorlagen .fld-Div im geklonten Dialog-Template
 * @param {string} bereichID Bereich der Schicht, für die Vorschlags-Chips
 * @param {function} onPick wird mit dem vollständigen Vorlagen-Objekt aufgerufen, wenn eine Vorlage gewählt wurde
 * @param {object} [options]
 * @param {boolean} [options.zeigeSchnellauswahl=true] zeigt die bis zu drei Vorschlags-Chips vor dem
 * „Weitere …“-Trigger (Schicht-Editor). Im Vorlage-Editor (`false`) gibt es nur den durchsuchbaren,
 * nach Bereich gruppierten Single-Trigger im `.inp`-Select-Look — eine Vorlage kann sich nicht selbst
 * als Schnellauswahl vorschlagen (siehe EDITORS.md §0).
 * @param {function} [options.onNeueVorlage] wird beim Klick auf "Neue Vorlage anlegen …" statt des
 * Standardverhaltens (öffnet den Vorlage-Editor neu über window.tb.erstelleSchichtVorlagenInputFeld)
 * aufgerufen. Für den Vorlage-Editor selbst übergeben (siehe toolbar.js) — dort soll der bereits offene
 * Dialog NICHT neu aufgebaut werden, weil man oft bewusst von einer geladenen Vorlage ausgehen und sie
 * unter neuem Namen speichern will; der Callback kappt dort nur die _id-Bindung.
 * @returns {{ setzeAktiveVorlage: function(string): void }} `setzeAktiveVorlage` markiert (ohne
 * `onPick` erneut aufzurufen) eine VorlagenID als aktiv, z.B. beim Vorbefüllen aus einer bereits
 * bestehenden Vorlage.
 */
async function renderVorlagenPicker(container, bereichID, onPick, options = {}) {
	const { zeigeSchnellauswahl = true, onNeueVorlage } = options;
	const alleVorlagen = await schichtenService.holeSchichtVorlagen();
	const vorschlaege = zeigeSchnellauswahl ? alleVorlagen.filter((v) => v.BereichID === bereichID).slice(0, 3) : [];

	let aktiveVorlageID = null;

	const wrap = document.createElement('div');
	wrap.className = zeigeSchnellauswahl ? 'qchips vpickwrap' : 'vpickwrap vselect';
	container.appendChild(wrap);

	const zeitLabel = (v) => {
		if (v.OffenesEnde && v.OffenerBeginn) return '';
		if (v.OffenesEnde) return `ab ${dayjs(v.SchichtVon).tz().format('HH:mm')}`;
		if (v.OffenerBeginn) return `bis ${dayjs(v.SchichtBis).tz().format('HH:mm')}`;
		return `${dayjs(v.SchichtVon).tz().format('HH:mm')}–${dayjs(v.SchichtBis).tz().format('HH:mm')}`;
	};

	// Nur im Single-Trigger-Modus (Vorlage-Editor) gesetzt — zeigt den Namen der aktiven Vorlage an,
	// da es dort (anders als bei den Chips) kein eigenes Element gibt, das seinen "on"-Zustand trägt.
	let triggerLabel;

	function markiereAktiv(vorlageID) {
		aktiveVorlageID = vorlageID;
		wrap.querySelectorAll('.qpick[data-id]').forEach((chip) => chip.classList.toggle('on', chip.dataset.id === vorlageID));
		popover.querySelectorAll('.vopt[data-id]').forEach((opt) => opt.classList.toggle('on', opt.dataset.id === vorlageID));
		if (triggerLabel) {
			const vorlage = alleVorlagen.find((v) => v._id === vorlageID);
			triggerLabel.textContent = vorlage ? `${vorlage.Titel} · ${zeitLabel(vorlage)}` : VORLAGE_TRIGGER_PLATZHALTER;
			triggerLabel.closest('.vselect-trigger').classList.toggle('ph', !vorlage);
		}
	}

	function waehleVorlage(vorlage) {
		markiereAktiv(vorlage._id);
		onPick(vorlage);
		popover.hidden = true;
	}

	let trigger;
	if (zeigeSchnellauswahl) {
		vorschlaege.forEach((v) => {
			const chip = document.createElement('div');
			chip.className = 'qpick';
			chip.dataset.id = v._id;
			chip.innerHTML = `<span class="qt">${secureHTML(v.Titel)}</span><span class="qm">${zeitLabel(v)}</span>`;
			chip.addEventListener('click', () => waehleVorlage(v));
			wrap.appendChild(chip);
		});

		trigger = document.createElement('div');
		trigger.className = 'qpick more';
		trigger.innerHTML = '<i class="bi bi-search"></i>Weitere …';
		wrap.appendChild(trigger);
	} else {
		trigger = document.createElement('button');
		trigger.type = 'button';
		trigger.className = 'inp ph vselect-trigger';
		trigger.innerHTML = `<span class="vselect-label">${VORLAGE_TRIGGER_PLATZHALTER}</span><i class="bi bi-chevron-down"></i>`;
		triggerLabel = trigger.querySelector('.vselect-label');
		wrap.appendChild(trigger);
	}

	// ── Popover ──
	const popover = document.createElement('div');
	popover.className = 'vpop';
	popover.hidden = true;
	wrap.appendChild(popover);

	const searchWrap = document.createElement('div');
	searchWrap.className = 'vsearch';
	searchWrap.innerHTML = '<i class="bi bi-search"></i><input type="text" placeholder="Vorlage suchen …">';
	popover.appendChild(searchWrap);
	const searchInput = searchWrap.querySelector('input');

	const list = document.createElement('div');
	list.className = 'vlist';
	popover.appendChild(list);

	const gruppen = new Map();
	alleVorlagen.forEach((v) => {
		const gruppenName = v.Bereich || 'Sonstige';
		if (!gruppen.has(gruppenName)) gruppen.set(gruppenName, []);
		gruppen.get(gruppenName).push(v);
	});

	function baueListe(filterText) {
		list.innerHTML = '';
		const filter = filterText.trim().toLowerCase();
		let sichtbareOptionen = 0;
		gruppen.forEach((vorlagen, gruppenName) => {
			const treffer = vorlagen.filter((v) => !filter
				|| v.Titel.toLowerCase().includes(filter)
				|| zeitLabel(v).toLowerCase().includes(filter));
			if (treffer.length === 0) return;
			const gruppenTitel = document.createElement('div');
			gruppenTitel.className = 'vgrp';
			gruppenTitel.innerText = gruppenName;
			list.appendChild(gruppenTitel);
			treffer.forEach((v) => {
				const opt = document.createElement('div');
				opt.className = `vopt${v._id === aktiveVorlageID ? ' on' : ''}`;
				opt.dataset.id = v._id;
				opt.innerHTML = `<span class="vt">${secureHTML(v.Titel)}</span><span class="vm">${zeitLabel(v)}</span>`;
				opt.addEventListener('click', () => waehleVorlage(v));
				list.appendChild(opt);
				sichtbareOptionen += 1;
			});
		});
		if (sichtbareOptionen === 0) {
			const leer = document.createElement('div');
			leer.className = 'vhint';
			leer.style.padding = '8px';
			leer.innerText = 'Keine Vorlage gefunden.';
			list.appendChild(leer);
		}
	}

	const vfoot = document.createElement('div');
	vfoot.className = 'vfoot';
	vfoot.innerHTML = '<i class="bi bi-plus-circle"></i>Neue Vorlage anlegen …';
	vfoot.addEventListener('click', () => {
		popover.hidden = true;
		// Keine Vorlage mehr als "aktiv" markiert (Chip/Popover-Zustand bzw. Single-Trigger-Label).
		markiereAktiv(null);
		if (onNeueVorlage) {
			// Vorlage-Editor (zeigeSchnellauswahl=false): NICHT den ganzen Dialog neu aufbauen — man will
			// oft bewusst von einer bereits geladenen Vorlage ausgehen und sie unter neuem Namen/Inhalt
			// speichern, statt bei null anzufangen. onNeueVorlage kappt nur die _id-Bindung (siehe
			// toolbar.js:erstelleSchichtVorlagenInputFeld), die restlichen Formularfelder bleiben unverändert.
			onNeueVorlage();
		} else {
			// Schicht-Editor (zeigeSchnellauswahl=true): hier gibt es keine "geladene Vorlage" zum
			// Weiterbearbeiten (die Vorlage wird nur auf die Schicht angewendet, nicht referenziert) —
			// öffnet stattdessen den eigenständigen Vorlage-Editor für eine komplett neue Vorlage.
			window.tb.erstelleSchichtVorlagenInputFeld();
		}
	});
	popover.appendChild(vfoot);

	searchInput.addEventListener('input', () => baueListe(searchInput.value));
	searchInput.addEventListener('keydown', (event) => {
		if (event.key === 'Escape') {
			popover.hidden = true;
		} else if (event.key === 'ArrowDown' || event.key === 'Enter') {
			event.preventDefault();
			list.querySelector('.vopt')?.focus?.();
			const erste = list.querySelector('.vopt');
			if (event.key === 'Enter' && erste) erste.click();
			else erste?.classList.add('active');
		}
	});

	function oeffnePopover() {
		baueListe('');
		popover.hidden = false;
		searchInput.value = '';
		searchInput.focus();
	}

	trigger.addEventListener('click', (event) => {
		event.stopPropagation();
		if (popover.hidden) oeffnePopover();
		else popover.hidden = true;
	});

	document.addEventListener('click', (event) => {
		if (popover.hidden) return;
		if (!wrap.contains(event.target)) popover.hidden = true;
	});
	document.addEventListener('keydown', (event) => {
		if (event.key === 'Escape' && !popover.hidden) popover.hidden = true;
	});

	return {
		setzeAktiveVorlage: markiereAktiv,
	};
}

/**
 * Wendet eine gewählte Vorlage auf die Felder des (bereits offenen) Schicht- oder Vorlage-Editors an:
 * Titel, Schichtfarbe, Art (Ganztags/OffenerBeginn/OffenesEnde) und Zeitblöcke.
 * @param {object} vorlage
 * @param {HTMLElement} dialog das geklonte Dialog-Template
 * @param {string} titleSelector Selector des Titel-Inputs — im Schicht-Editor `#input-schicht-titel`,
 * im Vorlage-Editor `#input-vorlage-titel`
 */
function wendeVorlageAn(vorlage, dialog, titleSelector = '#input-schicht-titel') {
	dialog.querySelector(titleSelector).value = vorlage.Titel;
	const farbInput = dialog.querySelector('#input-schicht-farbe');
	farbInput.value = vorlage.SchichtFarbe ? vorlage.SchichtFarbe : '#0071bd';
	farbInput.dispatchEvent(new Event('input'));

	const ganztags = dialog.querySelector('#input-schicht-ganztags');
	const offenerBeginn = dialog.querySelector('#input-schicht-offener-beginn');
	const offenesEnde = dialog.querySelector('#input-schicht-offenes-ende');
	ganztags.checked = !!vorlage.Ganztags;
	offenerBeginn.checked = !!vorlage.OffenerBeginn;
	offenesEnde.checked = !!vorlage.OffenesEnde;
	// Segmented Control passend nachziehen (siehe schichtEditor.js:initArtSegment für die gleiche Zuordnung)
	const seg = dialog.querySelector('#input-art-seg');
	let art = 'fest';
	if (ganztags.checked) art = 'ganztags';
	else if (offenerBeginn.checked && offenesEnde.checked) art = 'ganztags';
	else if (offenerBeginn.checked) art = 'offenerBeginn';
	else if (offenesEnde.checked) art = 'offenesEnde';
	seg?.querySelectorAll('.o').forEach((o) => o.classList.toggle('on', o.dataset.art === art));

	erstelleZeitblockTabelle(vorlage, 0);
}

export { renderVorlagenPicker, wendeVorlageAn };
